import { useInfiniteQuery } from '@tanstack/react-query';

import type { SortDirection } from '@cast/design-system';
import type { ClmWorkloadFilter, ClmListEventsResponse } from '@cast/types';
import { QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { makeIsEnabled } from '@cast/utils';

const CLM_EVENTS_QUERY_KEY = 'clm-events';

const getClmEventsQueryKey = ({
  clusterId,
  filter,
  sortField,
  sortOrder,
  pageLimit,
}: {
  clusterId?: string;
  filter?: ClmWorkloadFilter;
  sortField?: string;
  sortOrder?: SortDirection;
  pageLimit: number;
}) => [
  CLM_EVENTS_QUERY_KEY,
  clusterId,
  filter,
  sortField,
  sortOrder,
  pageLimit,
];

type Params = {
  clusterId: string;
  filter: ClmWorkloadFilter;
  sortField?: string;
  sortOrder?: SortDirection;
  pageLimit?: number;
};

type UseClmEventsQueryArgs = {
  params: Params;
  options?: QueryOptions<ClmListEventsResponse>;
};

const DEFAULT_PAGE_LIMIT = 50;

export const useClmEventsInfiniteQuery = ({
  params,
  options,
}: UseClmEventsQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;
  const {
    clusterId,
    filter,
    sortField,
    sortOrder,
    pageLimit = DEFAULT_PAGE_LIMIT,
  } = params;

  return useInfiniteQuery({
    queryKey: getClmEventsQueryKey({
      clusterId,
      filter,
      sortField,
      sortOrder,
      pageLimit,
    }),
    queryFn: async ({ pageParam }) => {
      const { data: responseData } =
        await apiClient.costReport.listContainerLiveMigrationEvents({
          organizationId: organizationId!,
          clusterId,
          containerLiveMigrationFilter: filter,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          sortField,
          sortOrder,
        });

      return responseData;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, options?.enabled),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextPageCursor || undefined,
    select: (data) => {
      return {
        items: data.pages.flatMap((page) => page.items ?? []),
        totalCount: data.pages[0]?.totalCount ?? 0,
      };
    },
  });
};
